import './App.css';

import { BosQueryClientProvider } from '@bos/api';
import { LocaleProvider, useLocale } from '@bos/locale-provider';
import type { MobilityUiConfig } from '@bos/mobility-ui';
import { MobilityUiProvider } from '@bos/mobility-ui';
import { SnackbarProvider } from '@bos/snackbar';
import { UiProvider } from '@bos/ui';

import { AppGridList } from './components/AppGridList';
import { AppBaseIcon } from './components/AppIcon/components/AppBaseIcon';
import { AppBaseRadioItem } from './components/AppRadioItem/AppBaseRadioItem';
import { AppBaseText } from './components/AppText/AppBaseText';
import { AppBaseTooltip } from './components/AppTooltip/AppBaseTooltip';
import { snackbarConfig } from './components/Snackbar/snackbarConfig';
import { AppBaseDateField } from './components/form/AppDateField/AppBaseDateField';
import { AppBaseRadioGroupField } from './components/form/AppRadioGroupField/AppBaseRadioGroupField';
import { appSizes } from './const/appSizes';
import { appStaticColors } from './const/colors/appStaticColors';
import { TokenProvider } from './context/TokenProvider/TokenProvider';
import { DynatraceProvider } from './lib/dynatrace/DynatraceProvider';
import { initSentry } from './lib/sentry/initSentry';
import { ZodTranslationProvider } from './lib/zod/ZodTranslationProvider';
import TypesafeI18n from './locale/i18n-react';
import { loadAllLocales } from './locale/i18n-util.sync';
import { AdvancedClientSearchProvider } from './pages/layouts/AppLayout/components/AdvancedClientSearch/AdvancedClientSearchProvider';
import { AppRouterProvider } from './router/router';

loadAllLocales();
initSentry();

document.body.style.backgroundColor = appStaticColors.appBackground;

const mobilityUiConfig: MobilityUiConfig = {
	DateField: AppBaseDateField,
	GridList: AppGridList,
	Icon: AppBaseIcon,
	RadioItem: AppBaseRadioItem,
	RadioGroupField: AppBaseRadioGroupField,
	Text: AppBaseText,
	Tooltip: AppBaseTooltip,
};

const AppWithLocale = () => {
	const { locale } = useLocale();

	return (
		<UiProvider breakpoints={appSizes.breakpoints}>
			<MobilityUiProvider config={mobilityUiConfig}>
				<BosQueryClientProvider>
					<TokenProvider>
						<DynatraceProvider />
						<TypesafeI18n key={locale} locale={locale}>
							<ZodTranslationProvider>
								<SnackbarProvider config={snackbarConfig}>
									<AdvancedClientSearchProvider>
										<AppRouterProvider />
									</AdvancedClientSearchProvider>
								</SnackbarProvider>
							</ZodTranslationProvider>
						</TypesafeI18n>
					</TokenProvider>
				</BosQueryClientProvider>
			</MobilityUiProvider>
		</UiProvider>
	);
};

export const App = () => {
	return (
		<LocaleProvider>
			<AppWithLocale />
		</LocaleProvider>
	);
};
